import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AllocDto, ColumnDto, Extra, ItemDto, LogDto } from '../../shared/api';
import { INSTALL, QC, colList, fmtQty, type Install, type Qc } from '../../shared/domain';
import { del, get, patch, post, refresh } from '../api';
import { Combo, Err, Modal, PlaceSelect, errMsg, num, pl, useApp, when } from './ui';

/** Runs a save, shows errors in the dialog and closes it on success. */
function useSave(onDone: () => void) {
  const { toast } = useApp();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      await refresh();
      toast(msg);
      onDone();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, err, run };
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="field">
      {label}
      {children}
      {hint && <span className="meta">{hint}</span>}
    </label>
  );
}

/** Inputs for the custom columns. */
function ExtraInputs({ cols, value, onChange }: { cols: ColumnDto[]; value: Extra; onChange: (v: Extra) => void }) {
  const { d } = useApp();
  return (
    <>
      {cols.map((c) => {
        const v = value[c.id] ?? '';
        const set = (x: string | number) => onChange({ ...value, [c.id]: x });
        return (
          <Field key={c.id} label={c.label}>
            {c.kind === 'dropdown' ? (
              <Combo live value={String(v)} list={d.b.options[colList(c.id)] ?? []} onCommit={set} />
            ) : (
              <input inputMode={c.kind === 'number' ? 'decimal' : undefined} value={String(v)} onChange={(e) => set(e.target.value)} />
            )}
          </Field>
        );
      })}
    </>
  );
}

interface Form {
  name: string;
  placeId: number | null;
  shelfId: number | null;
  type: string;
  qc: Qc;
  project: string;
  installation: Install;
  resource: string;
  qty: string;
  minQty: string;
  extra: Extra;
  note: string;
}

const formOf = (i?: ItemDto): Form => ({
  name: i?.name ?? '',
  placeId: i?.placeId ?? null,
  shelfId: i?.shelfId ?? null,
  type: i?.type ?? '',
  qc: i?.qc ?? 'Not needed',
  project: i?.project ?? '',
  installation: i?.installation ?? 'Not needed',
  resource: i?.resource ?? '',
  qty: i ? fmtQty(i.qty) : '',
  minQty: i?.minQty != null ? fmtQty(i.minQty) : '',
  extra: i?.extra ?? {},
  note: i?.note ?? '',
});

function bodyOf(f: Form) {
  return { ...f, qty: f.qty.trim() === '' ? 0 : num(f.qty), minQty: f.minQty.trim() === '' ? null : num(f.minQty) };
}

function ItemFields({ f, set }: { f: Form; set: (p: Partial<Form>) => void }) {
  const { d } = useApp();
  const o = d.b.options;
  return (
    <div className="fgrid">
      <Field label="Name *">
        <Combo live value={f.name} list={d.names} onCommit={(v) => set({ name: v })} placeholder="Pick a name or type a new one" allowEmpty={false} />
      </Field>
      <Field label="Place">
        <PlaceSelect placeId={f.placeId} shelfId={f.shelfId} onChange={(placeId, shelfId) => set({ placeId, shelfId })} />
      </Field>
      <Field label="Type">
        <Combo live value={f.type} list={o.type ?? []} onCommit={(v) => set({ type: v })} />
      </Field>
      <Field label="QC">
        <select value={f.qc} onChange={(e) => set({ qc: e.target.value as Qc })}>
          {QC.map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>
      </Field>
      <Field label="Project">
        <Combo live value={f.project} list={o.project ?? []} onCommit={(v) => set({ project: v })} />
      </Field>
      <Field label="Installation">
        <select value={f.installation} onChange={(e) => set({ installation: e.target.value as Install })}>
          {INSTALL.map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>
      </Field>
      <Field label="Resource">
        <Combo live value={f.resource} list={o.resource ?? []} onCommit={(v) => set({ resource: v })} placeholder="Supplier or source" />
      </Field>
      <Field label="QTY in the warehouse *">
        <input inputMode="decimal" value={f.qty} onChange={(e) => set({ qty: e.target.value })} />
      </Field>
      <Field label="Minimum" hint="Alert in the bell when QTY reaches this. Leave empty for no alert.">
        <input inputMode="decimal" value={f.minQty} onChange={(e) => set({ minQty: e.target.value })} />
      </Field>
      <ExtraInputs cols={d.b.columns} value={f.extra} onChange={(extra) => set({ extra })} />
      <Field label="Note">
        <textarea value={f.note} onChange={(e) => set({ note: e.target.value })} />
      </Field>
    </div>
  );
}

export function NewItemDialog({ onClose, preset }: { onClose: () => void; preset?: { placeId: number; shelfId: number | null } }) {
  const [f, setF] = useState<Form>({ ...formOf(), ...(preset ?? {}) });
  const { busy, err, run } = useSave(onClose);
  const set = (p: Partial<Form>) => setF((x) => ({ ...x, ...p }));
  return (
    <Modal
      title="New item"
      onClose={onClose}
      wide
      foot={
        <button className="btn primary" disabled={busy || !f.name.trim()} onClick={() => void run(() => post('/api/items', bodyOf(f)), `${f.name} added`)}>
          Save item
        </button>
      }
    >
      <ItemFields f={f} set={set} />
      <Err e={err} />
    </Modal>
  );
}

export function AddDialog({ item, onClose }: { item: ItemDto; onClose: () => void }) {
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const { busy, err, run } = useSave(onClose);
  const n = num(qty);
  return (
    <Modal
      title={`Add to ${item.name}`}
      onClose={onClose}
      foot={
        <button className="btn primary" disabled={busy || !(n > 0)} onClick={() => void run(() => post(`/api/items/${item.id}/add`, { qty: n, note }), `Added ${fmtQty(n)} ${item.name}`)}>
          Add {n > 0 ? fmtQty(n) : ''}
        </button>
      }
    >
      <p className="meta">Units that came in, e.g. bought. Now in the warehouse: {fmtQty(item.qty)}.</p>
      <div className="fgrid">
        <Field label="How many? *">
          <input autoFocus inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </Field>
        <Field label="Note (e.g. order or supplier)">
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      {n > 0 && <p className="meta">After this: {fmtQty(item.qty + n)} in the warehouse.</p>}
      <Err e={err} />
    </Modal>
  );
}

export function TakeDialog({ item, onClose }: { item: ItemDto; onClose: () => void }) {
  const { d } = useApp();
  const [qty, setQty] = useState('');
  const [mode, setMode] = useState<'project' | 'other'>('project');
  const [project, setProject] = useState(item.project);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [move, setMove] = useState(false);
  const [loc, setLoc] = useState({ placeId: item.placeId, shelfId: item.shelfId });
  const [more, setMore] = useState(false);
  const [qc, setQc] = useState<Qc>(item.qc);
  const [installation, setInstallation] = useState<Install>(item.installation === 'Not needed' ? 'Not needed' : 'Pending');
  const [resource, setResource] = useState(item.resource);
  const [extra, setExtra] = useState<Extra>(item.extra);
  const { busy, err, run } = useSave(onClose);
  const n = num(qty);
  const ok = n > 0 && n <= item.qty + 1e-9 && (mode === 'project' ? !!project.trim() : !!reason.trim());
  const save = () =>
    run(
      () =>
        post(
          `/api/items/${item.id}/take`,
          mode === 'project'
            ? { qty: n, project, note, ...(move ? loc : {}), ...(more ? { qc, installation, resource, extra } : {}) }
            : { qty: n, reason, note },
        ),
      mode === 'project' ? `${fmtQty(n)} ${item.name} assigned to ${project}` : `${fmtQty(n)} ${item.name} taken out`,
    );
  return (
    <Modal
      title={`Take from ${item.name}`}
      onClose={onClose}
      wide
      foot={
        <button className="btn primary" disabled={busy || !ok} onClick={() => void save()}>
          {mode === 'project' ? 'Assign to project' : 'Take out'}
        </button>
      }
    >
      <p className="meta">
        In the warehouse now: <b>{fmtQty(item.qty)}</b> at {d.where(item.placeId, item.shelfId) || 'no place'}.
      </p>
      <div className="seg" role="radiogroup">
        <button className={`btn ${mode === 'project' ? 'on' : ''}`} onClick={() => setMode('project')} role="radio" aria-checked={mode === 'project'}>
          For a project
        </button>
        <button className={`btn ${mode === 'other' ? 'on' : ''}`} onClick={() => setMode('other')} role="radio" aria-checked={mode === 'other'}>
          Other reason (broken, lost, sold …)
        </button>
      </div>
      <div className="fgrid">
        <Field label="How many? *">
          <input autoFocus inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </Field>
        {mode === 'project' ? (
          <Field label="Project *">
            <Combo live value={project} list={d.b.options.project ?? []} onCommit={setProject} allowEmpty={false} />
          </Field>
        ) : (
          <Field label="Why? *">
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. broken" />
          </Field>
        )}
        <Field label="Note">
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      {n > item.qty + 1e-9 && <p className="err">Only {fmtQty(item.qty)} in the warehouse.</p>}
      {mode === 'project' && (
        <>
          <label className="check">
            <input type="checkbox" checked={move} onChange={(e) => setMove(e.target.checked)} /> Put these units in another place
          </label>
          {move && (
            <div className="fgrid">
              <Field label="New place">
                <PlaceSelect placeId={loc.placeId} shelfId={loc.shelfId} onChange={(placeId, shelfId) => setLoc({ placeId, shelfId })} />
              </Field>
            </div>
          )}
          <label className="check">
            <input type="checkbox" checked={more} onChange={(e) => setMore(e.target.checked)} /> Change other columns for these units
          </label>
          {more && (
            <div className="fgrid">
              <Field label="QC">
                <select value={qc} onChange={(e) => setQc(e.target.value as Qc)}>
                  {QC.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </Field>
              <Field label="Installation">
                <select value={installation} onChange={(e) => setInstallation(e.target.value as Install)}>
                  {INSTALL.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </Field>
              <Field label="Resource">
                <Combo live value={resource} list={d.b.options.resource ?? []} onCommit={setResource} />
              </Field>
              <ExtraInputs cols={d.b.columns} value={extra} onChange={setExtra} />
            </div>
          )}
        </>
      )}
      {ok && <p className="meta">After this: {fmtQty(item.qty - n)} in the warehouse.</p>}
      <Err e={err} />
    </Modal>
  );
}

export function ReturnDialog({ alloc, item, onClose }: { alloc: AllocDto; item: ItemDto; onClose: () => void }) {
  const [qty, setQty] = useState(fmtQty(alloc.qty));
  const [note, setNote] = useState('');
  const { busy, err, run } = useSave(onClose);
  const n = num(qty);
  return (
    <Modal
      title={`Return ${item.name} from ${alloc.project}`}
      onClose={onClose}
      foot={
        <button className="btn primary" disabled={busy || !(n > 0 && n <= alloc.qty + 1e-9)} onClick={() => void run(() => post(`/api/allocations/${alloc.id}/return`, { qty: n, note }), `${fmtQty(n)} returned to the warehouse`)}>
          Return to the warehouse
        </button>
      }
    >
      <p className="meta">
        {fmtQty(alloc.qty)} assigned to {alloc.project}. Returned units go back to the warehouse QTY.
      </p>
      <div className="fgrid">
        <Field label="How many? *">
          <input autoFocus inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </Field>
        <Field label="Note">
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      <Err e={err} />
    </Modal>
  );
}

/** Item card: all values, the project assignments (children) and the full history. */
export function ItemDialog({ item, onClose }: { item: ItemDto; onClose: () => void }) {
  const { d, openAdd, openTake, openReturn, toast } = useApp();
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState<Form>(formOf(item));
  const { busy, err, run } = useSave(() => setEdit(false));
  const log = useQuery({ queryKey: ['log', item.id, item.last?.at], queryFn: () => get<LogDto[]>(`/api/items/${item.id}/log`) });
  const used = d.used(item);
  const remove = async () => {
    if (!confirm(`Delete ${item.name}${item.allocations.length ? ` and its ${pl(item.allocations.length, 'project assignment')}` : ''}? The history stays in the log.`)) return;
    try {
      await del(`/api/items/${item.id}`);
      await refresh();
      toast(`${item.name} deleted`);
      onClose();
    } catch (e) {
      toast(errMsg(e));
    }
  };
  const extraTxt = (e: Extra) =>
    d.b.columns
      .filter((c) => e[c.id] != null && e[c.id] !== '')
      .map((c) => `${c.label}: ${e[c.id]}`)
      .join(' · ');
  return (
    <Modal
      title={item.name}
      onClose={onClose}
      wide
      foot={
        d.isAdmin &&
        (edit ? (
          <>
            <button className="btn" onClick={() => (setEdit(false), setF(formOf(item)))}>
              Cancel
            </button>
            <button className="btn primary" disabled={busy || !f.name.trim()} onClick={() => void run(() => patch(`/api/items/${item.id}`, bodyOf(f)), 'Saved')}>
              Save changes
            </button>
          </>
        ) : (
          <>
            <button className="btn danger" onClick={() => void remove()}>
              Delete item
            </button>
            <span className="grow" />
            <button className="btn" onClick={() => setEdit(true)}>
              Edit
            </button>
            <button className="btn" onClick={() => openAdd(item.id)}>
              + Add
            </button>
            <button className="btn primary" disabled={item.qty <= 0} onClick={() => openTake(item.id)}>
              − Take
            </button>
          </>
        ))
      }
    >
      {edit ? (
        <>
          <ItemFields f={f} set={(p) => setF((x) => ({ ...x, ...p }))} />
          <Err e={err} />
        </>
      ) : (
        <>
          <dl className="kv">
            <dt>In the warehouse</dt>
            <dd>
              <b>{fmtQty(item.qty)}</b>
              {item.minQty != null && <span className={`tag ${item.qty <= item.minQty ? 'bad' : 'soft'}`} style={{ marginLeft: 8 }}>minimum {fmtQty(item.minQty)}</span>}
            </dd>
            <dt>Used for projects</dt>
            <dd>{fmtQty(used)}</dd>
            <dt>Place</dt>
            <dd>{d.where(item.placeId, item.shelfId) || '–'}</dd>
            <dt>Type</dt>
            <dd>{item.type || '–'}</dd>
            <dt>QC</dt>
            <dd>{item.qc}</dd>
            <dt>Project</dt>
            <dd>{item.project || '–'}</dd>
            <dt>Installation</dt>
            <dd>{item.installation}</dd>
            <dt>Resource</dt>
            <dd>{item.resource || '–'}</dd>
            {d.b.columns.map((c) => (
              <span key={c.id} style={{ display: 'contents' }}>
                <dt>{c.label}</dt>
                <dd>{item.extra[c.id] ?? '–'}</dd>
              </span>
            ))}
            {item.note && (
              <>
                <dt>Note</dt>
                <dd style={{ whiteSpace: 'pre-wrap' }}>{item.note}</dd>
              </>
            )}
          </dl>
          <h4>Used for projects</h4>
          {!item.allocations.length ? (
            <p className="meta">Not used for any project yet.</p>
          ) : (
            <div className="scroll">
              <table className="mini">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>QTY</th>
                    <th>Place</th>
                    <th>Installation</th>
                    <th>QC</th>
                    <th>By</th>
                    {d.isAdmin && <th />}
                  </tr>
                </thead>
                <tbody>
                  {item.allocations.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <b>{a.project}</b>
                        {(a.note || extraTxt(a.extra)) && <div className="meta">{[a.note, extraTxt(a.extra)].filter(Boolean).join(' · ')}</div>}
                      </td>
                      <td>{fmtQty(a.qty)}</td>
                      <td>{d.where(a.placeId, a.shelfId) || '–'}</td>
                      <td>{a.installation}</td>
                      <td>{a.qc}</td>
                      <td className="meta">
                        {a.createdBy}, {when(a.createdAt)}
                      </td>
                      {d.isAdmin && (
                        <td>
                          <button className="btn small" onClick={() => openReturn(a.id)}>
                            Return
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <h4>History</h4>
          <LogTable list={log.data} loading={log.isLoading} />
        </>
      )}
    </Modal>
  );
}

export function LogTable({ list, loading, showItem }: { list?: LogDto[]; loading?: boolean; showItem?: boolean }) {
  const { openItem, d } = useApp();
  if (loading) return <p className="meta">Loading…</p>;
  if (!list?.length) return <p className="meta">Nothing yet.</p>;
  return (
    <div className="scroll">
      <table className="mini log">
        <thead>
          <tr>
            <th>When</th>
            <th>Who</th>
            {showItem && <th>Item</th>}
            <th>What</th>
            <th>Project / why</th>
          </tr>
        </thead>
        <tbody>
          {list.map((l) => (
            <tr key={l.id}>
              <td className="meta nowrap">{when(l.at)}</td>
              <td>{l.user}</td>
              {showItem && (
                <td>
                  {l.itemId && d.item(l.itemId) ? (
                    <button className="linkbtn" onClick={() => openItem(l.itemId!)}>
                      {l.itemName}
                    </button>
                  ) : (
                    l.itemName || '–'
                  )}
                </td>
              )}
              <td>
                <b>{l.action}</b>
                {l.details && <div className="meta">{l.details}</div>}
              </td>
              <td>{[l.project, l.reason].filter(Boolean).join(' · ') || '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
